/**
 * Ctextfx scramble scheduler — one module-level earliest-deadline clock for
 * every unresolved glyph on the page.
 *
 * A due tick writes `node.textContent` directly and advances that glyph's own
 * randomized deadline; it never touches React state. Mount/unmount batches
 * coalesce into a single microtask so a 600-glyph placeholder rebuilds the
 * timer once instead of 600 times.
 */

import {
  randomScrambleDelayMs,
  randomScrambleGlyph,
} from "./Ctextfx.scramble-random";

export interface ScrambleGlyphRegistration {
  getExcludedGlyphs: () => ReadonlySet<string>;
  glyphs: string;
  maxDelayMs: number;
  minDelayMs: number;
  node: HTMLElement;
}

interface ScheduledGlyph extends ScrambleGlyphRegistration {
  currentGlyph: string;
  nextChangeAt: number;
}

/** Caps scheduler wakeups at 20Hz; a write can land up to this much late. */
const SCRAMBLE_SCHEDULER_QUANTUM_MS = 50;

const scheduledGlyphs = new Set<ScheduledGlyph>();
let scrambleTimer: number | null = null;
let scrambleScheduleQueued = false;

function earliestScheduledAt(dueTimes: readonly number[]): number | null {
  const earliest = dueTimes.reduce(
    (candidate, dueAt) => Math.min(candidate, dueAt),
    Number.POSITIVE_INFINITY,
  );
  return Number.isFinite(earliest) ? earliest : null;
}

function scheduleNextScramble(): void {
  if (scrambleTimer != null) window.clearTimeout(scrambleTimer);
  scrambleTimer = null;
  const earliest = earliestScheduledAt(
    Array.from(scheduledGlyphs, (glyph) => glyph.nextChangeAt),
  );
  if (earliest == null) return;
  scrambleTimer = window.setTimeout(
    tickScramble,
    Math.max(SCRAMBLE_SCHEDULER_QUANTUM_MS, earliest - performance.now()),
  );
}

function queueScrambleSchedule(): void {
  if (scrambleScheduleQueued) return;
  scrambleScheduleQueued = true;
  queueMicrotask(() => {
    scrambleScheduleQueued = false;
    scheduleNextScramble();
  });
}

function tickScramble(): void {
  scrambleTimer = null;
  const now = performance.now();
  scheduledGlyphs.forEach((glyph) => {
    if (now < glyph.nextChangeAt) return;
    glyph.currentGlyph = randomScrambleGlyph({
      exclude: new Set([...glyph.getExcludedGlyphs(), glyph.currentGlyph]),
      glyphs: glyph.glyphs,
    });
    glyph.node.textContent = glyph.currentGlyph;
    glyph.nextChangeAt =
      now +
      randomScrambleDelayMs({
        scrambleMaxMs: glyph.maxDelayMs,
        scrambleMinMs: glyph.minDelayMs,
      });
  });
  scheduleNextScramble();
}

/** Subscribes one glyph node; the returned disposer removes it and rebuilds
 *  the shared timer (clearing it once the last subscriber leaves). */
export function registerScrambleGlyph(
  registration: ScrambleGlyphRegistration,
): () => void {
  const scheduled: ScheduledGlyph = {
    ...registration,
    currentGlyph: registration.node.textContent ?? "",
    nextChangeAt:
      performance.now() +
      randomScrambleDelayMs({
        scrambleMaxMs: registration.maxDelayMs,
        scrambleMinMs: registration.minDelayMs,
      }),
  };
  scheduledGlyphs.add(scheduled);
  queueScrambleSchedule();
  return (): void => {
    scheduledGlyphs.delete(scheduled);
    queueScrambleSchedule();
  };
}
